// QR Kiosk v2 — KV2-9 nameplate language selector (owner ruling D16, 2026-08-14).
// The N2 canvas always drew an "EN ▾" control next to the PUBLIC NAMEPLATE chip. It shipped
// omitted because the shared chrome/LanguageChip.tsx is a NO-OP off kiosk machine routes:
// its handler navigates to /public-v2/machines/:no/settings and does nothing when the path
// carries no machine number — which is exactly this route (/public-v2/nameplate/:code).
//
// DELIBERATELY NOT a fix to the shared chrome/LanguageChip: that chip has live kiosk
// consumers (KChrome) whose behaviour must not change under this ticket. Making the shared
// chip switch language in place — for every kiosk surface — is a separate ticket.
//
// Self-contained and NON-PORTALED on purpose: this page does not mount KioskV2Layout, so
// KioskOverlayContext is null here (shared/kioskOverlayRoot.tsx) and a Radix portal would
// land on document.body, OUTSIDE the .qr-kiosk-v2-root / .p-claude / .tier-anon scope where
// every CSS var in this file is defined — the menu would render unstyled. The menu is instead
// an absolutely-positioned child of its own wrapper inside that scope; no ancestor in the
// chrome band sets overflow: hidden, so nothing clips it.
//
// PERSISTENCE — reused, not reinvented. core/i18n.ts configures i18next-browser-languagedetector
// with detection.order ["localStorage", "navigator", "htmlTag"], lookupLocalStorage "i18nextLng"
// and caches ["localStorage"]. i18n.changeLanguage() therefore writes localStorage["i18nextLng"]
// itself and the detector reads it back on the next visit — the SAME key the rest of the app
// (including SettingsV2's language rows) already uses. Do not add a second storage key here.
//
// NO window.location.reload() — SettingsV2 does one, this page must not. A reload re-issues
// the nameplate GET, and every 200 writes a NameplateScanEvent server-side; the per-machine
// scan count is the Tajmac pilot KPI, so a reload here is a double-counted scan (the same
// reason NameplateV2 guards its fetch behind a ref). react-i18next re-renders every
// useTranslation consumer on `languageChanged`, which i18next emits only AFTER the new
// namespace JSON has loaded — so the labels flip in place, with no reload and no flash.
import React, { useCallback, useEffect, useRef, useState } from "react";
import useTranslation, { SupportedLanguage } from "../../../shared/hooks/useTranslation";
import { LANGUAGE_NATIVE_NAMES } from "../../../shared/i18n/languages";
import { Glyph, GD } from "../shared/glyphs";

/** Native display names — NEVER translated (same rule as SettingsV2 §7.1). Imported from
 *  shared/i18n/languages.ts, which is store-free (NOT from pages/SettingsV2: that module drags
 *  Redux + account context into what is deliberately an anonymous, store-free page). */
const LANG_NAMES: Record<SupportedLanguage, string> = LANGUAGE_NATIVE_NAMES;

// The option list is NOT hard-coded any more (USER-LANG-IT-PL-1 L3-FE): it is
// useTranslation().getSupportedLanguages() — this host's languages, in the app-wide menu order
// sk, en, cs, de, it, pl (owner ruling D7 of 2026-10-31 supersedes the D16 EN / SK / CS order).
// This page is pre-login, so the per-host list is the only thing standing between a browser
// `it-IT` on a host without Italian and a half-translated nameplate.

const ITEM_SELECTOR = '[role="menuitemradio"]';

export function NameplateLanguageSelect() {
  const { i18n, changeLanguage, getSupportedLanguages, translate } = useTranslation("public");
  const languages = getSupportedLanguages();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Normalized-code rule — the v1 regional-code bug (en-US → en) never enters v2.
  const current = ((i18n.language || "en").split("-")[0] as SupportedLanguage) || "en";

  const items = () =>
    menuRef.current ? Array.from(menuRef.current.querySelectorAll<HTMLButtonElement>(ITEM_SELECTOR)) : [];

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  }, []);

  // Outside-pointer + Escape dismissal, bound ONLY while open.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(true);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open, close]);

  // Move focus into the list on open — keyboard and screen-reader users land on the current
  // language, not on an unfocused menu they cannot reach.
  useEffect(() => {
    if (!open) return;
    const list = items();
    const checked = list.find((el) => el.getAttribute("aria-checked") === "true");
    (checked || list[0])?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Home" && e.key !== "End") return;
    const list = items();
    if (list.length === 0) return;
    e.preventDefault();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      e.key === "Home"
        ? 0
        : e.key === "End"
        ? list.length - 1
        : e.key === "ArrowDown"
        ? (at + 1 + list.length) % list.length
        : (at - 1 + list.length) % list.length;
    list[next].focus();
  };

  const select = (lng: SupportedLanguage) => {
    close(true);
    if (lng === current) return;
    // A failed load leaves the current language in place. The chrome band has no error
    // surface and an unchanged label is the honest fallback — SettingsV2 owns the loud path.
    changeLanguage(lng).catch(() => undefined);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      {/* The DRAWN control is the canvas's small mono "EN ▾"; the <button> pads out to a 44px
          tap target with transparent padding, the same trick the escalation pills use. */}
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${translate("kioskLanguage")}: ${current.toUpperCase()}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        style={{
          height: 44,
          padding: "0 4px",
          margin: 0,
          border: 0,
          background: "transparent",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          fontFamily: "var(--font-mono)",
          fontSize: 10.5,
          lineHeight: "14px",
          letterSpacing: "0.04em",
          color: "var(--muted)",
          whiteSpace: "nowrap",
        }}
      >
        {current.toUpperCase()} {"▾"}
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={translate("kioskLanguage")}
          onKeyDown={onMenuKeyDown}
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 3,
            minWidth: 152,
            padding: 4,
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 10,
            boxShadow: "var(--shadow)",
            zIndex: 20,
          }}
        >
          {languages.map((lng) => {
            const active = lng === current;
            return (
              <button
                key={lng}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => select(lng)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  height: 40,
                  padding: "0 8px",
                  border: 0,
                  borderRadius: 7,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                  background: active ? "var(--tier-accent-soft)" : "transparent",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--muted)",
                    width: 22,
                    flexShrink: 0,
                  }}
                >
                  {lng}
                </span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13.5,
                    lineHeight: "18px",
                    fontWeight: active ? 700 : 500,
                    color: "var(--ink)",
                  }}
                >
                  {LANG_NAMES[lng]}
                </span>
                {active && (
                  <span style={{ color: "var(--tier-accent)", display: "inline-flex", flexShrink: 0 }}>
                    <Glyph d={GD.check} size={15} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default NameplateLanguageSelect;
